import { Lock, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { BoardSort, WeeklyBoard } from '@obraplan/core/domain/planning/weekly-board';
import { Hours } from '@obraplan/core/domain/shared/hours';
import { Badge } from '@/components/ui/badge';
import { HoursProgress } from '@/components/ui/hours-progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { EmptyState } from '@/components/feedback/states';
import { cn } from '@/lib/cn';
import {
  DayTotalCell,
  HoursCell,
  QuantityCell,
  type AllocationCreate,
  type AllocationEdit,
} from './planner-cells';
import { formatTotalQuantity } from './planner-format';
import { alternar, groupBoardByTeam, teamOfCell } from './planner-teams';
import { TeamGridBlock, TeamHeldMark } from './WeeklyPlannerTeams';

export type { AllocationCreate, AllocationEdit };

/**
 * O quadro da semana.
 *
 * ```text
 *                              seg    ter    qua    qui    sex   semana
 * João da Silva — Pedreiro                                        44h/44h
 *   Alvenaria — 2º Pavimento    4h     4h     9h     9h     8h      34h
 *   Estrutural — 1º Pavimento   5h     5h      —      —      —      10h
 *   Total do dia               9h/9h  9h/9h  9h/9h  9h/9h  8h/8h
 * ```
 *
 * Cada célula é um **dia de trabalho** numa atividade — é assim que o encarregado monta
 * o serviço, e é o que torna o conflito verificável: a jornada existe por dia. A linha
 * "total do dia" é onde o excesso aparece, porque é ali que ele acontece.
 *
 * A rolagem horizontal fica **dentro** do quadro — o `body` nunca rola na horizontal
 * (§4.4), o que mantém a tela usável no tablet do canteiro.
 *
 * Na obra por equipe (Fase 30, D10), a alocação com a marca da equipe sobe para a linha da
 * equipe, que abre nas pessoas (`planner-teams`); a pessoa só com alocação da equipe não vem
 * sozinha. O resto da grade é o de sempre.
 *
 * Nenhuma regra de capacidade vive aqui. O aviso que aparece **antes** do envio sai de
 * `capacityStatusOf`, a mesma função pura que o caso de uso usa para recusar — é isso
 * que impede a tela de dizer "cabe" enquanto o sistema responde "não cabe".
 */

export interface WeeklyPlannerGridProps {
  board: WeeklyBoard;
  sort: BoardSort;
  onSortChange: (sort: BoardSort) => void;
  readOnly: boolean;
  canPlan: boolean;
  onCreate: (input: AllocationCreate) => void;
  onEdit: (edit: AllocationEdit) => void;
  onDelete: (edit: AllocationEdit) => void;
  onAdd: (resourceId: string) => void;
  /** Abre "Alocar equipe" com a equipe da linha escolhida (Fase 30). Só na obra por equipe. */
  onAddTeam?: (teamId: string) => void;
}

const SORT_OPTIONS: readonly { id: BoardSort; label: string }[] = [
  { id: 'BALANCE', label: 'Maior saldo' },
  { id: 'NAME', label: 'Nome' },
  { id: 'TEAM', label: 'Equipe' },
];

export function WeeklyPlannerGrid({
  board,
  sort,
  onSortChange,
  readOnly,
  canPlan,
  onCreate,
  onEdit,
  onDelete,
  onAdd,
  onAddTeam,
}: WeeklyPlannerGridProps) {
  const editable = !readOnly && canPlan;
  // D10 — a alocação com a marca da equipe vai para a linha da equipe; o resto, como sempre.
  const agrupado = useMemo(() => groupBoardByTeam(board), [board]);
  const nomesDasEquipes = useMemo(
    () => new Map(board.teams.map((team) => [team.teamId as string, team.name])),
    [board.teams],
  );
  /** As equipes abertas nas pessoas. Fechadas por padrão: a equipe é uma linha. */
  const [abertas, setAbertas] = useState<ReadonlySet<string>>(() => new Set());

  if (board.rows.length === 0) {
    return (
      <EmptyState
        title="Nenhum recurso cadastrado"
        description="Cadastre funcionários ou serviços terceirizados na tela de Mão de obra para poder planejar a semana."
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-(--color-content-muted)">Ordenar por</span>
        {SORT_OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-pressed={sort === option.id}
            onClick={() => onSortChange(option.id)}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs',
              sort === option.id
                ? 'bg-(--color-surface-muted) font-medium text-(--color-content)'
                : 'text-(--color-content-muted) hover:bg-(--color-surface-muted)',
            )}
          >
            {option.label}
          </button>
        ))}
        {readOnly && (
          <Badge tone="neutral" icon={<Lock aria-hidden="true" />}>
            Semana fechada — somente leitura
          </Badge>
        )}
      </div>

      <TableWrapper className="rounded-(--radius-card) border border-(--color-border-subtle)">
        <Table>
          <caption className="sr-only">
            Alocação de horas por recurso, atividade e dia na semana {board.isoWeek} de{' '}
            {board.isoYear}
          </caption>
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 z-10 min-w-60 bg-(--color-surface-raised)">
                Recurso e atividade
              </TableHead>
              {board.days.map((day) => (
                <TableHead
                  key={day.date}
                  className={cn('min-w-20 text-center', !day.isWorkday && 'opacity-50')}
                >
                  <span className="block">{day.weekdayLabel}</span>
                  <span className="block text-[10px] font-normal normal-case">
                    {day.shortLabel}
                  </span>
                </TableHead>
              ))}
              <TableHead className="min-w-44">Semana</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {agrupado.teams.map((group) => (
              <TeamGridBlock
                key={group.team.teamId}
                group={group}
                days={board.days}
                editable={editable}
                expanded={abertas.has(group.team.teamId)}
                onToggle={() => setAbertas((atual) => alternar(atual, group.team.teamId))}
                onEdit={onEdit}
                onDelete={onDelete}
                onAdd={onAdd}
                {...(onAddTeam === undefined ? {} : { onAddTeam })}
              />
            ))}
            {agrupado.rows.map((row) => (
              <ResourceBlock
                key={row.resource.resourceId}
                row={row}
                days={board.days}
                teamNames={nomesDasEquipes}
                editable={editable}
                onCreate={onCreate}
                onEdit={onEdit}
                onDelete={onDelete}
                onAdd={onAdd}
              />
            ))}
          </TableBody>
        </Table>
      </TableWrapper>
    </div>
  );
}

type Row = WeeklyBoard['rows'][number];

interface ResourceBlockProps {
  row: Row;
  days: WeeklyBoard['days'];
  /** O nome da equipe de cada marca: o dia da equipe, na linha da pessoa, diz de quem é. */
  teamNames: ReadonlyMap<string, string>;
  editable: boolean;
  onCreate: (input: AllocationCreate) => void;
  onEdit: (edit: AllocationEdit) => void;
  onDelete: (edit: AllocationEdit) => void;
  onAdd: (resourceId: string) => void;
}

function ResourceBlock({
  row,
  days,
  teamNames,
  editable,
  onCreate,
  onEdit,
  onDelete,
  onAdd,
}: ResourceBlockProps) {
  const columnCount = days.length + 2;
  /**
   * As horas que estão nos campos agora, por linha e dia — gravadas ou ainda não.
   *
   * É o que acende a meta do dia no mesmo instante em que a hora é digitada, em vez de
   * esperar a alocação ir e voltar do servidor.
   */
  const [digitadas, setDigitadas] = useState<Readonly<Record<string, number>>>({});

  return (
    <>
      {/* Cabeçalho do recurso: nome, situação e o saldo **da semana**. */}
      <TableRow className="bg-(--color-surface-muted)">
        <TableCell className="sticky left-0 z-10 bg-(--color-surface-muted)">
          <div className="flex flex-col">
            <span className="font-medium">{row.resource.name}</span>
            <span className="text-xs text-(--color-content-muted)">
              {row.resource.detail}
              {row.resource.teamName !== undefined && ` · ${row.resource.teamName}`}
            </span>
            {!row.resource.isAllocatable && (
              <span className="mt-1 text-xs text-(--color-state-warn)">
                Inativo — não recebe nova alocação
              </span>
            )}
          </div>
        </TableCell>
        <TableCell colSpan={days.length}>
          {editable && row.resource.isAllocatable && (
            <button
              type="button"
              onClick={() => onAdd(row.resource.resourceId)}
              className="inline-flex items-center gap-1 rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-2 py-1 text-xs hover:bg-(--color-surface-raised)"
            >
              <Plus className="size-3" aria-hidden="true" />
              Adicionar atividade
            </button>
          )}
        </TableCell>
        <TableCell>
          <HoursProgress balance={row.balance} />
        </TableCell>
      </TableRow>

      {row.activities.length === 0 ? (
        <TableRow>
          <TableCell colSpan={columnCount} className="pl-8 text-sm text-(--color-content-muted)">
            Sem alocação nesta semana.
          </TableCell>
        </TableRow>
      ) : (
        row.activities.flatMap((activityRow, indice) => {
          const chave = `${activityRow.activity.activityId}|${activityRow.service?.serviceId ?? 'sem'}`;
          /*
           * O cabeçalho da frente, uma vez por frente.
           *
           * As linhas já vêm ordenadas por frente e depois por serviço, então "mudou de
           * frente" é comparar com a anterior. O cabeçalho não carrega número nenhum de
           * propósito: ele é lugar, e quem trabalha é o serviço embaixo dele. É também o
           * que torna visível a troca de disciplina no meio da semana — que é rara, e
           * quando acontece precisa saltar aos olhos.
           */
          const abreFrente =
            indice === 0 ||
            row.activities[indice - 1]?.activity.activityId !== activityRow.activity.activityId;

          /*
           * A linha sem serviço é somente leitura.
           *
           * Ela não sabe o que se fez ali, então não tem meta nem medição — e deixar
           * digitar horas nela produziria mais do mesmo. O botão de apagar continua na
           * célula: o caminho é apagar e refazer com o serviço certo.
           */
          const linhaEditavel =
            editable &&
            activityRow.service !== undefined &&
            // Disciplina desativada não recebe hora nova; o que já foi lançado fica à vista.
            activityRow.activity.disciplineInactive !== true;
          /** A unidade em que esta linha mede — do serviço (RN-232). Ausente, sem meta. */
          const medida =
            activityRow.service?.unitName === undefined
              ? undefined
              : {
                  nome: activityRow.service.unitName,
                  casas: activityRow.service.unitDecimals ?? 2,
                };

          return [
            ...(abreFrente
              ? [
                  <TableRow key={`${activityRow.activity.activityId}-frente`}>
                    <TableCell
                      colSpan={columnCount}
                      className="sticky left-0 z-10 bg-(--color-surface-raised) pl-8 pt-3"
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{activityRow.activity.label}</span>
                        <span className="text-xs text-(--color-content-muted)">
                          {activityRow.activity.towerName}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>,
                ]
              : []),
            <TableRow key={chave}>
              <TableCell className="sticky left-0 z-10 bg-(--color-surface-raised) pl-12">
                <div className="flex flex-col">
                  <span className="text-sm">{activityRow.service?.name ?? 'Sem serviço'}</span>
                  {activityRow.service === undefined ? (
                    <span className="text-xs text-(--color-content-muted)">
                      Planejada antes do cadastro de serviços — não mede. Apague e refaça.
                    </span>
                  ) : activityRow.activity.disciplineInactive === true ? (
                    <span className="text-xs text-(--color-content-muted)">
                      disciplina desativada — não recebe hora nova
                    </span>
                  ) : activityRow.service.unitName === undefined ? (
                    <span className="text-xs text-(--color-content-muted)">
                      sem unidade — cadastre em Configurações → Serviços
                    </span>
                  ) : (
                    <span className="text-xs text-(--color-content-muted)">
                      medida em {activityRow.service.unitName}
                    </span>
                  )}
                </div>
              </TableCell>

              {activityRow.cells.map((cell, index) => {
                const dayTotal = row.dayTotals[index]!;
                const day = days[index]!;
                const daEquipe = teamOfCell(cell);
                if (daEquipe !== undefined) {
                  return (
                    <TableCell key={cell.date} className="text-center">
                      <TeamHeldMark cell={cell} teamName={teamNames.get(daEquipe) ?? 'Equipe'} />
                    </TableCell>
                  );
                }
                return (
                  <TableCell key={cell.date} className="text-center">
                    <HoursCell
                      quarterHours={cell.allocation?.quarterHours ?? 0}
                      dayTotal={dayTotal}
                      isWorkday={day.isWorkday}
                      exceedsCapacity={cell.allocation?.exceedsCapacity ?? false}
                      justification={cell.allocation?.justification}
                      resourceName={row.resource.name}
                      activityLabel={activityRow.activity.label}
                      dayLabel={`${day.weekdayLabel} ${day.shortLabel}`}
                      editable={linhaEditavel}
                      onDraftChange={(quarterHours) => {
                        const onde = `${chave}|${cell.date}`;
                        setDigitadas((atual) =>
                          (atual[onde] ?? 0) === quarterHours
                            ? atual
                            : { ...atual, [onde]: quarterHours },
                        );
                      }}
                      onCommit={(quarterHours) => {
                        if (cell.allocation === undefined) {
                          onCreate({
                            resourceId: row.resource.resourceId,
                            activityId: activityRow.activity.activityId,
                            // A linha já sabe o serviço: digitar 4h na terça da fôrma é
                            // fôrma, e não uma segunda linha sem serviço na mesma frente.
                            ...(activityRow.service === undefined
                              ? {}
                              : { serviceId: activityRow.service.serviceId }),
                            date: cell.date,
                            quarterHours,
                          });
                          return;
                        }
                        if (quarterHours === 0) {
                          onDelete({
                            allocationId: cell.allocation.id,
                            quarterHours: cell.allocation.quarterHours,
                            version: cell.allocation.version,
                          });
                          return;
                        }
                        onEdit({
                          allocationId: cell.allocation.id,
                          quarterHours,
                          version: cell.allocation.version,
                        });
                      }}
                      {...(cell.allocation === undefined
                        ? {}
                        : {
                            onDelete: () => {
                              onDelete({
                                allocationId: cell.allocation!.id,
                                quarterHours: cell.allocation!.quarterHours,
                                version: cell.allocation!.version,
                              });
                            },
                          })}
                    />
                  </TableCell>
                );
              })}

              <TableCell className="tabular text-sm">
                {Hours.ofQuarters(activityRow.plannedQuarterHours).format()}
              </TableCell>
            </TableRow>,

            /*
             * A meta ganha uma linha própria, e só quando a frente é medida.
             *
             * Dois campos empilhados dentro da mesma célula dobrariam a altura da grade
             * inteira — inclusive das frentes que ninguém mede. Uma segunda linha aparece
             * onde faz sentido e some onde não faz.
             */
            ...(medida === undefined
              ? []
              : [
                  <TableRow key={`${chave}-quantidade`}>
                    <TableCell className="sticky left-0 z-10 bg-(--color-surface-raised) pl-16">
                      <span className="text-xs text-(--color-content-muted)">
                        Meta ({medida.nome})
                      </span>
                    </TableCell>

                    {activityRow.cells.map((cell, index) => {
                      const day = days[index]!;
                      const comHora = (digitadas[`${chave}|${cell.date}`] ?? 0) > 0;
                      if (teamOfCell(cell) !== undefined) {
                        // A meta deste dia é da equipe, e está na linha dela.
                        return (
                          <TableCell key={`${cell.date}-quantidade`} className="text-center">
                            <span className="text-xs text-(--color-content-muted)">—</span>
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell key={`${cell.date}-quantidade`} className="text-center">
                          <QuantityCell
                            plannedQuantity={cell.allocation?.plannedQuantity}
                            executedQuantity={cell.allocation?.executedQuantity}
                            unitName={medida.nome}
                            decimals={medida.casas}
                            resourceName={row.resource.name}
                            activityLabel={activityRow.activity.label}
                            dayLabel={`${day.weekdayLabel} ${day.shortLabel}`}
                            editable={linhaEditavel && (cell.allocation !== undefined || comHora)}
                            pending={cell.allocation === undefined}
                            onCommit={(plannedQuantity) => {
                              if (cell.allocation === undefined) return;
                              onEdit({
                                allocationId: cell.allocation.id,
                                quarterHours: cell.allocation.quarterHours,
                                version: cell.allocation.version,
                                plannedQuantity,
                              });
                            }}
                          />
                        </TableCell>
                      );
                    })}

                    <TableCell className="tabular text-xs text-(--color-content-muted)">
                      {formatTotalQuantity(
                        {
                          cells: activityRow.cells.filter((cell) => teamOfCell(cell) === undefined),
                        },
                        medida.casas,
                      )}
                    </TableCell>
                  </TableRow>,
                ]),
          ];
        })
      )}

      {/* Total do dia — é aqui que o excesso de jornada aparece (RN-044). */}
      {row.activities.length > 0 && (
        <TableRow>
          <TableCell className="sticky left-0 z-10 bg-(--color-surface-raised) pl-8 text-xs uppercase tracking-wide text-(--color-content-muted)">
            Total do dia
          </TableCell>
          {row.dayTotals.map((dayTotal) => (
            <TableCell key={dayTotal.date} className="text-center">
              <DayTotalCell total={dayTotal} />
            </TableCell>
          ))}
          <TableCell />
        </TableRow>
      )}
    </>
  );
}
